/* eslint-disable no-unsafe-optional-chaining */
import { PageSize, TableTypes } from '@/constant';
import { useGetListData } from '@/hooks';
import { CommonActions } from '@/stores';
import { MyPageSelectors } from '@/stores/my-page';
import { arrayToJson, insertIf } from '@/utils';
import { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { DefaultOptionType } from 'antd/es/select';

import { updateHistoryOptionsMap } from '../config';
import { IUpdateHistoryProps } from '../type';
import { getColumnsHistoryMobile } from './config';

export const useLogic = ({
	refTableName,
	id: propsId,
	optionalFields = [],
}: IUpdateHistoryProps) => {
	const { id } = useParams<{ id: string }>();

	const { t } = useTranslation(['updateHistory', 'national']);

	const { isForeignRole } = useSelector(MyPageSelectors.selectDataMyPage);

	const historyUpdateOptions = useMemo(() => {
		const newHistoryUpdateOptions = [
			...updateHistoryOptionsMap[refTableName],
			...insertIf(
				!!optionalFields?.length,
				...optionalFields?.map((op) => ({
					value: `CUSTOM_FIELD_${op?.fieldNo}`,
					label: op?.label,
				}))
			),
		];

		return newHistoryUpdateOptions;
	}, [refTableName, optionalFields]);

	const historyUpdateOptionJson = useMemo(
		() => arrayToJson(historyUpdateOptions, 'value'),
		[historyUpdateOptions]
	);

	const dispatch = useDispatch();

	const {
		list: dataSource,
		fetching: loading,
		total,
		params: reduxParams,
	} = useGetListData({
		type: TableTypes.UPDATE_HISTORY,
	});

	useEffect(() => {
		if (!propsId && !id) return;

		fetchData({ page: 1 });
	}, [propsId, id]);

	const fetchData = useCallback(
		(params: Partial<typeof reduxParams>) => {
			if (id || propsId) {
				const newParams = {
					...reduxParams,
					...params,
				};

				dispatch(
					CommonActions.getUpdateHistoryListRequest({
						...newParams,
						pageSize: PageSize.XXXS,
						refTableId: Number(id ?? propsId),
						refTableName,
						allData: false,
						fieldName: newParams?.fieldName,
					})
				);
			}
		},
		[id, propsId, reduxParams, refTableName]
	);

	const onPageSizeChange = useCallback(
		(pageSize: DefaultOptionType['value']) => {
			fetchData({
				page: 1,
				pageSize: Number(pageSize),
			});
		},
		[fetchData]
	);

	const onPageChange = useCallback(
		(page: number) => {
			fetchData({
				page,
			});
		},
		[fetchData]
	);

	const columns = useMemo(() => {
		return getColumnsHistoryMobile(
			dataSource,
			{
				...historyUpdateOptionJson,
			},
			t,
			isForeignRole
		);
	}, [dataSource, historyUpdateOptionJson, t, isForeignRole]);

	return {
		reduxParams,
		dataSource,
		total,
		loading,
		historyUpdateOptions,
		fetchData,
		onPageSizeChange,
		onPageChange,
		columns,
	};
};
